import styled from '@emotion/styled';
import { useMemo } from 'react';

import { GradientButton } from '../../../buttons/GradientButton';

import { useI18n } from '../../../../hooks/useI18nFormatters';

import { animationFade } from '../../../../theme/animations/animationFade';
import { publicUrl } from '../../../../theme/functions/publicUrl';
import { su } from '../../../../theme/functions/su';
import { glyph } from '../../../../theme/glyph';
import { motion } from '../../../../theme/motion';
import { theme } from '../../../../theme/theme';

export function BridgeRequiredView(props) {
  const { i18n } = useI18n();

  const isBasicDriver =
    props.driverId != null &&
    props.uri === 'homey:manager:vdevice' &&
    (props.driverId.includes('zigbee') ||
      props.driverId.includes('zwave') ||
      props.driverId.includes('infrared'));

  const connectivityMessage = useMemo(() => {
    const localConnectivities = {
      ble: 'Bluetooth',
      zwave: 'Z-Wave',
      zigbee: 'Zigbee',
      infrared: 'Infrared',
      rf433: '433 MHz',
    };

    // Filter out connectivities that are not in the localConnectivities object (e.g. Matter)
    const filteredConnectivities = props.connectivities.filter((connectivity) =>
      Object.keys(localConnectivities).includes(connectivity),
    );

    // Format the message as a list comma separated connectivities with the last one being 'or'
    return filteredConnectivities
      .map((connectivity) => localConnectivities[connectivity])
      .join(', ')
      .replace(/, ([^,]*)$/, ' or $1');
  }, [props.connectivities]);

  return (
    <BridgeRequiredView.Root>
      <BridgeRequiredView.Content>
        <BridgeRequiredView.Title>
          {i18n.messageFormatter('pair.bridgeRequiredTitle')}
        </BridgeRequiredView.Title>
        <BridgeRequiredView.Message>
          {isBasicDriver
            ? i18n.messageFormatter('pair.bridgeRequiredTechnologyMessage', {
                technology: props.driverInfo?.driverName,
              })
            : i18n.messageFormatter('pair.bridgeRequiredDeviceMessage', {
                device: props.driverInfo?.driverName,
                connectivity: connectivityMessage,
              })}
        </BridgeRequiredView.Message>
        <BridgeRequiredView.Image
          height="196"
          srcSet={`${publicUrl('img/pairing/homey-bridge.png')} 1x, ${publicUrl(
            'img/pairing/homey-bridge@2x.png',
          )} 2x`}
          src={publicUrl('img/pairing/homey-bridge.png')}
        />
      </BridgeRequiredView.Content>
      <BridgeRequiredView.Footer>
        <GradientButton
          styleWidth="full"
          styleColor="purple"
          elementType="a"
          target="_blank"
          href="https://homey.app/homey-bridge"
        >
          <GradientButton.Text>
            {i18n.messageFormatter('pair.bridgeRequiredButton')}
          </GradientButton.Text>
          <GradientButton.Icon
            source={glyph.core.squareArrowUpRight}
            color={theme.color.white}
            size={theme.icon.size_default}
          />
        </GradientButton>
      </BridgeRequiredView.Footer>
    </BridgeRequiredView.Root>
  );
}

BridgeRequiredView.Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: ${su(2)};

  width: 100%;
  overflow-y: auto;
  flex: 1 1 auto;
  animation: ${animationFade.in} ${motion.duration.slow} ease-out 1 forwards;
`;

BridgeRequiredView.Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

BridgeRequiredView.Title = styled.h1`
  margin-bottom: ${su(1)};
  text-align: center;
`;

BridgeRequiredView.Message = styled.div`
  text-align: center;
`;

BridgeRequiredView.Image = styled.img`
  display: block;
  max-height: 40vh;
  margin-top: ${su(6)};
`;

BridgeRequiredView.Footer = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
`;
